import { fold } from "fp-ts/lib/Either";
import { pipe } from "fp-ts/function";
import * as t from "io-ts";
import { PathReporter } from "io-ts/lib/PathReporter";

/** Jackson NON_NULL responses omit null fields; retain a consistent nullable model after decoding. */
export function nullableWhenOmitted<A, O>(codec: t.Type<A, O, unknown>) {
  const nullable = t.union([codec, t.null]);
  return new t.Type<A | null, O | null, unknown>(
    `NullableWhenOmitted<${codec.name}>`,
    nullable.is,
    (input, context) => nullable.validate(input === undefined ? null : input, context),
    nullable.encode
  );
}

/**
 * Validates the JSON payload.
 *
 * @param validator The io-ts validator.
 * @param input The JSON payload.
 * @retuns A promise that is fulfilled with the value of type T, or is rejected with the validation errors.
 */
export function validateJSON<T, O, I>(
  validator: t.Type<T, O, I>,
  input: I
): Promise<T> {
  const result = validator.decode(input);

  return pipe(
    result,
    fold(
      (errors) => {
        if (process.env.NODE_ENV !== 'production') {
          console.error(PathReporter.report(result));
        }
        return Promise.reject(`${errors.length} error(s) found`);
      },
      (value) => Promise.resolve(value)
    )
  );
}

/**
 * Validates the JSON payload.
 *
 * @param validator The io-ts validator.
 * @param input The JSON payload.
 * @returns A promise that is fulfilled with the value of type T, or is rejected with the validation errors.
 */
export function validateJSONNow<T, O, I>(
  validator: t.Type<T, O, I>,
  input: I
): T {
  const result = validator.decode(input);

  return pipe(
    result,
    fold(
      (errors) => {
        if (process.env.NODE_ENV !== 'production') {
          console.error(PathReporter.report(result));
        }
        throw new Error(`${errors.length} error(s) found`);
      },
      (value) => {
        return value;
      }
    )
  );
}
